import { useState } from 'react';
import { FormSheet } from '@/shared/components';
import type { ArrivalPlaceholderResponse } from '@/shared/api';
import { sameDraft, type ArrivalStepDraft } from './arrivalDraft';
import { arrivalProblem } from './arrivalProblem';
import { ArrivalStepForm } from './ArrivalStepForm';
import { ArrivalStepPreview } from './ArrivalStepPreview';
import { GUIDE_COPY } from './guideCopy';
import type { ArrivalScope } from './useArrivalGuideApi';
import { useArrivalPreview } from './useArrivalPreview';

interface Props {
  scope: ArrivalScope;
  /** Gesetzt beim Bearbeiten eines vorhandenen Schritts. */
  stepId?: string;
  initial: ArrivalStepDraft;
  placeholders: ArrivalPlaceholderResponse[];
  saving: boolean;
  error: string | null;
  onSave: (draft: ArrivalStepDraft) => void;
  onClose: () => void;
}

/**
 * Bearbeiten im Sheet, die Reihenfolge bleibt daneben sichtbar. Ein unbekanntes Feld weist der
 * Server ab — hier steht es dem Speichern schon vorher im Weg, statt als Meldung nach dem Klick.
 */
export function ArrivalStepSheet(props: Props) {
  const { scope, initial } = props;
  const guide = scope.guide ?? 'Arrival';
  const copy = GUIDE_COPY[guide];
  const [draft, setDraft] = useState(initial);
  const [previewPropertyId, setPreviewPropertyId] = useState<string | null>(null);
  const preview = useArrivalPreview(scope, draft, previewPropertyId);
  const unknown = preview.data?.unknownPlaceholders ?? [];
  const dirty = !sameDraft(draft, initial);
  const complete = !!draft.titleDe.trim() && !!draft.bodyDe.trim();

  return (
    <FormSheet
      title={props.stepId ? copy.editTitle : copy.newTitle}
      description={
        scope.kind === 'object'
          ? 'Gilt für alle Apartments dieses Objekts.'
          : 'Gilt nur für dieses Apartment; die anderen behalten die Fassung des Objekts.'
      }
      dirty={dirty}
      saving={props.saving}
      canSave={complete && unknown.length === 0 && (dirty || !props.stepId)}
      error={props.error}
      submitLabel={props.stepId ? 'Änderungen speichern' : copy.create}
      onSubmit={() => props.onSave(draft)}
      onClose={props.onClose}
    >
      <ArrivalStepForm
        guide={guide}
        draft={draft}
        placeholders={props.placeholders}
        onChange={setDraft}
        preview={
          <ArrivalStepPreview
            preview={preview.data}
            loading={preview.isFetching}
            error={preview.isError ? arrivalProblem(preview.error) : null}
            selectedPropertyId={previewPropertyId}
            onSelectProperty={setPreviewPropertyId}
          />
        }
      />
    </FormSheet>
  );
}
